import { useEffect, useState } from 'react'
import { Button } from './ui/button'
import { Checkbox } from './ui/checkbox'
import { Label } from './ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import type { OrgNoticeInfo } from './OnboardingDialog'

export interface OrgNoticeConsentDialogProps {
  open: boolean
  orgName?: string | null
  notice: OrgNoticeInfo
  submitting?: boolean
  error?: string
  loadError?: string
  onSubmit: (orgNoticeId: string) => void
  onRetry?: () => void
  onUseDifferentAccount?: () => void
}

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium underline underline-offset-2 text-[hsl(var(--color-primary-text))] transition-colors hover:text-[hsl(var(--color-primary-text-hover))]"
    >
      {children}
    </a>
  )
}

/**
 * Blocking dialog when an organization updates its notice after the user has
 * already completed first-login onboarding. Requires acknowledgement to continue.
 */
export function OrgNoticeConsentDialog({
  open,
  orgName,
  notice,
  submitting = false,
  error = '',
  loadError = '',
  onSubmit,
  onRetry,
  onUseDifferentAccount,
}: OrgNoticeConsentDialogProps) {
  const [noticeAccepted, setNoticeAccepted] = useState(false)

  useEffect(() => {
    if (open) {
      setNoticeAccepted(false)
    }
  }, [open, notice.id])

  const canSubmit = noticeAccepted && !loadError && !submitting

  return (
    <Dialog open={open}>
      <DialogContent
        className="[&>button.absolute]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Updated institution notice</DialogTitle>
          <DialogDescription>
            {orgName
              ? `${orgName} has updated its notice. Please review and acknowledge it to continue.`
              : 'Your institution has updated its notice. Please review and acknowledge it to continue.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-start gap-2">
            <Checkbox
              id="orgNoticeReconsent"
              checked={noticeAccepted}
              onCheckedChange={(checked) => setNoticeAccepted(checked === true)}
              disabled={submitting || Boolean(loadError)}
              className="-ml-1.5"
            />
            <Label
              htmlFor="orgNoticeReconsent"
              className="pt-1 text-sm leading-5 font-normal cursor-pointer"
            >
              I have read the{' '}
              <ExternalLink href={notice.url}>
                {orgName ? `${orgName} ${notice.title}` : notice.title}
              </ExternalLink>
            </Label>
          </div>

          {loadError && (
            <div className="space-y-3 rounded-md border border-[hsl(var(--color-destructive))] bg-[hsl(var(--color-destructive))]/10 px-4 py-3 text-sm text-[hsl(var(--color-destructive))]">
              <p>{loadError}</p>
              {onRetry && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onRetry}
                  disabled={submitting}
                >
                  Try again
                </Button>
              )}
            </div>
          )}

          {error && !loadError && (
            <div className="rounded-md border border-[hsl(var(--color-destructive))] bg-[hsl(var(--color-destructive))]/10 px-4 py-3 text-sm text-[hsl(var(--color-destructive))]">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Button
            onClick={() => {
              if (!canSubmit) return
              onSubmit(notice.id)
            }}
            disabled={!canSubmit}
            className="w-full"
          >
            {submitting ? 'Saving...' : 'Continue'}
          </Button>
          {onUseDifferentAccount && (
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={onUseDifferentAccount}
              disabled={submitting}
            >
              Use a different account
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
